import { ACCESS_TOKEN } from "../constants";
import { AuthService } from "./auth.service";
import { requestTokenRefresh } from "./api.service";

// Use relative URL to work with Vite proxy
const AUTH_API_URL = "/apis";

type RequestOptions = {
  url: string;
  method: string;
  body?: string;
};

type LoginRequest = {
  usernameOrEmail: string;
  password: string;
  [key: string]: any;
};

type SignupRequest = {
  name: string;
  username: string;
  email: string;
  password: string;
  [key: string]: any;
};

type SecurityQuestionRequest = {
  [key: string]: any;
};

type ChangePasswordRequest = {
  oldPassword: string;
  newPassword: string;
  [key: string]: any;
};

type ResetRequest = {
  email: string;
  [key: string]: any;
};

type UserQuestion = {
  [key: string]: any;
};

type UserFailedCount = {
  count: number;
  [key: string]: any;
};

type UserProfile = {
  [key: string]: any;
};

type ApiResponse<T = any> = T;

const request = <T = any>(options: RequestOptions): Promise<ApiResponse<T>> => {
  const headers = new Headers({
    "Content-Type": "application/json"
  });

  const accessToken = AuthService.token;
  if (accessToken) {
    headers.append("Authorization", "Bearer " + AuthService.token);
    headers.append("Accesstoken", AuthService.token);
    headers.append("Refreshtoken", AuthService.refreshToken || "");
  }

  const defaults = { headers: headers };
  const finalOptions = Object.assign({}, defaults, options);
  return fetch(finalOptions.url, finalOptions).then(response => {
    // Handle 401 — attempt token refresh if user was authenticated
    if (response.status === 401 && accessToken) {
      return new Promise<T>((resolve, reject) => {
        requestTokenRefresh(
          (newToken) => {
            // Retry the request with the new token
            headers.set("Authorization", "Bearer " + newToken);
            headers.set("Accesstoken", newToken);
            fetch(finalOptions.url, { ...finalOptions, headers })
              .then(r => r.json())
              .then(resolve)
              .catch(reject);
          },
          reject
        );
      });
    }

    const contentType = response.headers.get("content-type");

    // Check if response is JSON
    if (contentType && contentType.includes("application/json")) {
      return response.json().then(json => {
        if (!response.ok) {
          return Promise.reject(json);
        }
        return json;
      });
    } else {
      // Response is not JSON (likely HTML error page)
      return response.text().then(text => {
        console.error("API returned non-JSON response:", response.status, text.substring(0, 200));
        return Promise.reject({
          success: false,
          message: `Server error: ${response.status} - API endpoint returned non-JSON response`
        });
      });
    }
  });
};

export function login(loginRequest: LoginRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/signin",
    method: "POST",
    body: JSON.stringify(loginRequest)
  });
}

export function signout(signoutRequest: Record<string, any>): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/signout",
    method: "POST",
    body: JSON.stringify(signoutRequest)
  });
}

export function signup(signupRequest: SignupRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/signup",
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function securityQuestionUpdate(securityQuestionRequest: SecurityQuestionRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/securityQuestion",
    method: "POST",
    body: JSON.stringify(securityQuestionRequest)
  });
}

export function changePassword(changePasswordRequest: ChangePasswordRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/user/changePassword",
    method: "POST",
    body: JSON.stringify(changePasswordRequest)
  });
}

export function active(token: string): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/activate/" + token,
    method: "GET"
  });
}

export function reset(signupRequest: ResetRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/reset",
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function resetWithQuestion(signupRequest: ResetRequest): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/auth/resetWithQuestion",
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function checkUsernameAvailability(username: string): Promise<ApiResponse<{ available: boolean }>> {
  return request({
    url: AUTH_API_URL + "/user/checkUsernameAvailability?username=" + username,
    method: "GET"
  });
}

export function getUserQuestions(email: string): Promise<ApiResponse<UserQuestion[]>> {
  return request({
    url: AUTH_API_URL + "/auth/user/dto/questions?email=" + email,
    method: "GET"
  });
}

export function getUserFailedCount(email: string): Promise<ApiResponse<UserFailedCount>> {
  return request({
    url: AUTH_API_URL + "/auth/user/failed/count?email=" + email,
    method: "GET"
  });
}

export function checkEmailAvailability(email: string): Promise<ApiResponse<{ available: boolean }>> {
  return request({
    url: AUTH_API_URL + "/user/checkEmailAvailability?email=" + email,
    method: "GET"
  });
}

// Cache configuration for getCurrentUser
const USER_CACHE_KEY = 'cached_current_user';
const USER_CACHE_TIMESTAMP_KEY = 'cached_current_user_timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export function getCurrentUser(): Promise<ApiResponse<UserProfile>> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  if (!accessToken) {
    return Promise.reject("No access token set.");
  }

  // Check cache first
  const cachedUser = localStorage.getItem(USER_CACHE_KEY);
  const cachedTimestamp = localStorage.getItem(USER_CACHE_TIMESTAMP_KEY);

  if (cachedUser && cachedTimestamp) {
    const age = Date.now() - parseInt(cachedTimestamp, 10);
    if (age < CACHE_DURATION_MS) {
      console.log('⚡ Using cached user data (age: ' + Math.round(age / 1000) + 's)');
      return Promise.resolve(JSON.parse(cachedUser));
    } else {
      console.log('🔄 Cache expired (age: ' + Math.round(age / 1000) + 's), fetching fresh data');
    }
  }

  // Fetch fresh data
  return request({
    url: AUTH_API_URL + "/user/me",
    method: "GET"
  }).then((user: ApiResponse<UserProfile>) => {
    // Cache the result
    try {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
      localStorage.setItem(USER_CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('💾 Cached fresh user data');
    } catch (e) {
      console.warn('Failed to cache user data:', e);
    }
    return user;
  });
}

// Helper to clear user cache (call on logout)
export function clearUserCache(): void {
  localStorage.removeItem(USER_CACHE_KEY);
  localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
}

export function getUserProfile(username: string): Promise<ApiResponse<UserProfile>> {
  return request({
    url: AUTH_API_URL + "/users/" + username,
    method: "GET"
  });
}

export function getUserSourceByActiveToken(token: string): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/user/getUserSourceByActiveToken?token=" + token,
    method: "GET"
  });
}

export function getAllUsers(): Promise<ApiResponse<UserProfile[]>> {
  return request({
    url: AUTH_API_URL + "/admin/users",
    method: "GET"
  });
}

export function getAllAudit(): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + "/audits",
    method: "GET"
  });
}

// New Audit API functions for enhanced audit page

export type AuditFilterRequest = {
  page?: number;
  size?: number;
  searchTerm?: string;
  actionType?: string;
  entityName?: string;
  startDate?: number;
  endDate?: number;
  sortBy?: string;
  sortDirection?: string;
};

export type AuditDto = {
  id: string;
  entityId: string;
  entityIdInfo: string;
  entityName: string;
  actionType: string;
  auditor: string;
  ipAddress: string;
  location: string;
  dateAudit: string;
  timestamp: number;
};

export type AuditPageResponse = {
  content: AuditDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export type AuditStatistics = {
  totalLogins: number;
  totalLogouts: number;
  totalFailedLogins: number;
  totalSignups: number;
  totalPasswordResets: number;
  actionTypeCounts: Record<string, number>;
  dailyLogins: Record<string, number>;
  dailyFailedLogins: Record<string, number>;
  hourlyDistribution: Record<number, number>;
  topUsers: Record<string, number>;
  periodStart: number;
  periodEnd: number;
};

export type SecurityAlert = {
  alertType: string;
  severity: string;
  username: string;
  message: string;
  count: number;
  timestamp: number;
  ipAddress: string;
};

export function searchAuditPaginated(filter: AuditFilterRequest): Promise<ApiResponse<AuditPageResponse>> {
  return request({
    url: AUTH_API_URL + "/audits/search",
    method: "POST",
    body: JSON.stringify(filter)
  });
}

export function exportAuditToCsv(filter: AuditFilterRequest): Promise<Blob> {
  const headers = new Headers({
    "Content-Type": "application/json"
  });

  const accessToken = AuthService.token;
  if (accessToken) {
    headers.append("Authorization", "Bearer " + AuthService.token);
    headers.append("Accesstoken", AuthService.token);
    headers.append("Refreshtoken", AuthService.refreshToken || "");
  }

  return fetch(AUTH_API_URL + "/audits/export/csv", {
    method: "POST",
    headers: headers,
    body: JSON.stringify(filter)
  }).then(response => {
    if (!response.ok) {
      return Promise.reject("Failed to export CSV");
    }
    return response.blob();
  });
}

export function getAuditStatistics(startDate?: number, endDate?: number): Promise<ApiResponse<AuditStatistics>> {
  let url = AUTH_API_URL + "/audits/statistics";
  const params: string[] = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length > 0) url += "?" + params.join("&");

  return request({
    url: url,
    method: "GET"
  });
}

export function getSecurityAlerts(): Promise<ApiResponse<SecurityAlert[]>> {
  return request({
    url: AUTH_API_URL + "/audits/alerts",
    method: "GET"
  });
}

export function getAuditActionTypes(): Promise<ApiResponse<string[]>> {
  return request({
    url: AUTH_API_URL + "/audits/action-types",
    method: "GET"
  });
}

export function getAuditEntityNames(): Promise<ApiResponse<string[]>> {
  return request({
    url: AUTH_API_URL + "/audits/entity-names",
    method: "GET"
  });
}

export function getUserSessions(username: string, startDate?: number, endDate?: number): Promise<ApiResponse<any[]>> {
  let url = AUTH_API_URL + `/audits/sessions/${encodeURIComponent(username)}`;
  const params: string[] = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length > 0) url += "?" + params.join("&");

  return request({
    url: url,
    method: "GET"
  });
}

export function getAllUserTrial(): Promise<ApiResponse<UserProfile[]>> {
  return request({
    url: AUTH_API_URL + "/admin/users?trial=true",
    method: "GET"
  });
}

export function getUserNameByIds(data: string[]): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + `/users/ids`,
    method: "POST"
  });
}

export function deleteUser(id: string): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + `/users/${id}`,
    method: "DELETE"
  });
}

export function deleteUserProject(id: string): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + `/users/${id}`,
    method: "DELETE"
  });
}

export function editUser(data: Partial<UserProfile>): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + `/users`,
    method: "PUT",
    body: JSON.stringify(data)
  });
}

export function userLoggedIn(): Promise<ApiResponse> {
  return request({
    url: AUTH_API_URL + `/users/userLoggedIn`,
    method: "GET"
  });
}
